import React, { useEffect } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';

import { useTypedProjectCostContext } from './useTypedProjectCostContext';
import { LAST_COMPANY_STORAGE_KEY } from './utils';

const tabLinkClass = ({ isActive }: { isActive: boolean }) =>
  `nav-link${isActive ? ' active' : ''}`;

const ProjectsCostCompanyLayout: React.FC = () => {
  const { companyId } = useParams();
  const numericCompanyId = Number(companyId);
  const {
    getCompanyById,
    openNewCollaborator,
    openNewCompanyRoleRate,
    openNewCollaboratorRateForCompany,
    handleDeleteAllCollaboratorsByCompany,
  } = useTypedProjectCostContext<{
    getCompanyById: (id: number) => { id: number; name: string } | undefined;
    openNewCollaborator: (companyId?: string) => void;
    openNewCompanyRoleRate: (companyId?: string) => void;
    openNewCollaboratorRateForCompany: (companyId: number) => void;
    handleDeleteAllCollaboratorsByCompany: (companyId: number, companyName: string) => Promise<void>;
  }>();

  const company = Number.isFinite(numericCompanyId) ? getCompanyById(numericCompanyId) : undefined;

  useEffect(() => {
    if (company?.id) {
      try {
        localStorage.setItem(LAST_COMPANY_STORAGE_KEY, String(company.id));
      } catch {
        // ignore storage errors
      }
    }
  }, [company?.id]);

  if (!company) {
    return (
      <Alert variant="warning">
        Empresa não encontrada.{' '}
        <Link to="/projects/costs">Voltar para visão geral</Link>
      </Alert>
    );
  }

  const basePath = `/projects/costs/empresas/${company.id}`;

  return (
    <>
      <nav aria-label="breadcrumb" className="cost-breadcrumb mb-2">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/projects/costs">Visão geral</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {company.name}
          </li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h4 className="mb-0">{company.name}</h4>
        <div className="d-flex flex-wrap gap-2">
          <Button size="sm" onClick={() => openNewCollaborator(String(company.id))}>
            Novo colaborador
          </Button>
          <Button size="sm" onClick={() => openNewCompanyRoleRate(String(company.id))}>
            Nova regra empresa+função
          </Button>
          <Button size="sm" variant="outline-dark" onClick={() => openNewCollaboratorRateForCompany(company.id)}>
            Nova taxa temporal
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => void handleDeleteAllCollaboratorsByCompany(company.id, company.name)}
          >
            Excluir todos os colaboradores
          </Button>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <NavLink to={`${basePath}/resumo`} className={tabLinkClass}>Resumo</NavLink>
        </li>
        <li className="nav-item">
          <NavLink to={`${basePath}/colaboradores`} className={tabLinkClass}>Colaboradores</NavLink>
        </li>
        <li className="nav-item">
          <NavLink to={`${basePath}/regras`} className={tabLinkClass}>Regras</NavLink>
        </li>
        <li className="nav-item">
          <NavLink to={`${basePath}/taxas-temporais`} className={tabLinkClass}>Taxas temporais</NavLink>
        </li>
        <li className="nav-item">
          <NavLink to={`${basePath}/funcoes-usadas`} className={tabLinkClass}>Funções usadas</NavLink>
        </li>
      </ul>

      <Outlet />
    </>
  );
};

export default ProjectsCostCompanyLayout;
